import { Fragment, useCallback, useMemo, useState } from 'react';
import {
  Box,
  TextField,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Divider,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import { LoadingButton } from '@mui/lab';
import { orderBy, uniqBy } from 'lodash-es';
import { Converter } from './Converter.jsx';
import * as service from '../../../services/admin-service';
import { useAlerts } from '../../../hooks/use-alerts';
import { DownloadLink } from './DownloadLink';
import { SimpleTable } from '../../../components/table/SimpleTable';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import { useFeatureFlag } from '../../../hooks/use-feature-flag';

function dedupeUploads(jobs) {
  if (!jobs) return [];

  return jobs.map((job) => {
    const allUploads =
      job.rawFiles?.flatMap((file) => file.uploads || []) || [];
    const uploadFileCounts = new Map();

    allUploads.forEach((upload) => {
      uploadFileCounts.set(
        upload.objectId,
        (uploadFileCounts.get(upload.objectId) || 0) + 1,
      );
    });

    const uploads = orderBy(
      uniqBy(allUploads, 'objectId').map((upload) => ({
        ...upload,
        fileCount: uploadFileCounts.get(upload.objectId),
      })),
      ['createdAt'],
      ['desc'],
    );

    return { ...job, uploads };
  });
}

export function Lookup() {
  const isUseLeaf = useFeatureFlag('UseLeaf');
  const [working, setWorking] = useState(false);
  const [aaid, setAaid] = useState('');
  const [results, setResults] = useState();
  const [confirmDelete, setConfirmDelete] = useState();
  const [confirmResend, setConfirmResend] = useState();
  const { showError, showInfo } = useAlerts();
  const field = useMemo(() => results?.field, [results]);

  const yieldJobs = useMemo(() => dedupeUploads(results?.yield), [results]);

  const plantingJobs = useMemo(
    () => dedupeUploads(results?.planting),
    [results],
  );

  const lookupField = useCallback(
    (event) => {
      event.preventDefault();
      if (!aaid.trim()) return;
      setWorking(true);
      setResults(null);

      service
        .lookupUploads(aaid)
        .then(setResults)
        .catch(showError)
        .finally(() => setWorking(false));
    },
    [aaid, showError],
  );

  const deleteRawFile = useCallback(() => {
    if (!confirmDelete) return;

    const { type, id } = confirmDelete;
    setConfirmDelete(false);
    setWorking(true);

    service
      .deleteRawFile(type, id)
      .then(() => {
        setResults((current) => ({
          ...current,
          [type]: current[type].map((job) => ({
            ...job,
            rawFiles: job.rawFiles.filter((file) => file.objectId !== id),
          })),
        }));
      })
      .catch(showError)
      .finally(() => setWorking(false));
  }, [confirmDelete, showError]);

  const resendToLeaf = useCallback(() => {
    if (!confirmResend) return;

    const { type, id } = confirmResend;
    setConfirmResend(false);
    setWorking(true);

    service
      .resendToLeaf(type, id)
      .then(() => showInfo('Zip has been resent to leaf'))
      .catch(showError)
      .finally(() => setWorking(false));
  }, [confirmResend, showError, showInfo]);

  return (
    <>
      <Box
        sx={{
          px: 2,
          py: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <form onSubmit={lookupField}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
            <TextField
              value={aaid}
              disabled={working}
              onChange={(event) => setAaid(event.target.value)}
              label="aaid"
              size="small"
              sx={{ width: 450 }}
              autoFocus
            />
            <LoadingButton
              size="large"
              color="primary"
              variant="contained"
              sx={{ height: 'calc(100% - 2px)' }}
              disabled={!aaid.trim().length}
              loading={working}
              onClick={lookupField}
            >
              Lookup
            </LoadingButton>
          </Box>
        </form>
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {buildResults()}
        </Box>
      </Box>

      {confirmDelete && (
        <ConfirmationDialog
          title="Delete Raw File"
          message={`Are you sure you want to delete ${confirmDelete.filename}?`}
          handleClose={() => setConfirmDelete(false)}
          handleConfirm={deleteRawFile}
        />
      )}

      {confirmResend && (
        <ConfirmationDialog
          title="Resend to Leaf"
          message={`Are you sure you want to resend ${confirmResend.filename} to Leaf?`}
          handleClose={() => setConfirmResend(false)}
          handleConfirm={resendToLeaf}
        />
      )}
    </>
  );

  function buildResults() {
    if (!results) return null;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {buildFieldDetails()}
        {buildYieldDetails()}
        {buildPlantingDetails()}
      </Box>
    );
  }

  function buildFieldDetails() {
    return (
      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Operation
              </Typography>
              <Typography variant="body1">
                {field.grower?.operationName}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Organization
              </Typography>
              <Typography variant="body1">
                {field.grower?.organization?.name}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Field
              </Typography>
              <Typography variant="body1">{field.fieldName}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Farm
              </Typography>
              <Typography variant="body1">{field.farmName}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Acres
              </Typography>
              <Typography variant="body1">{field.acres?.toFixed(2)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Planned Crop
              </Typography>
              <Typography variant="body1">{field.plannedCrop}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }

  function buildYieldDetails() {
    if (!yieldJobs?.length) return null;
    return yieldJobs.map((job, index) =>
      buildJob(
        index,
        buildYieldHeader(job),
        buildYieldJobDetails(job),
        job,
        'yield',
      ),
    );
  }

  function buildPlantingDetails() {
    if (!plantingJobs?.length) return null;
    return plantingJobs.map((job, index) =>
      buildJob(
        index,
        buildPlantingHeader(job),
        buildPlantingJobDetails(job),
        job,
        'planting',
      ),
    );
  }

  function buildJob(index, header, details, job, type) {
    return (
      <Accordion key={index} variant="outlined" sx={{ borderRadius: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {header}
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {details}
            {buildJobUploads(job, type)}
            {buildJobRawFiles(job, type)}
            {buildJobMetadata(job)}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }

  function buildPlantingHeader(job) {
    const tfsLink = `/planting/${field.growerId.toUpperCase()}/${field.aaid.toUpperCase()}?year=${job.year}`;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          width: '100%',
        }}
      >
        <Typography>
          <Link
            href={tfsLink}
            color="inherit"
            target="_blank"
            onClick={(e) => e.stopPropagation()}
          >
            {`${job.year} ${job.crop} planting`}
          </Link>
        </Typography>
      </Box>
    );
  }

  function buildYieldHeader(job) {
    const tfsLink = `/yield-display/${field.growerId.toUpperCase()}/${field.aaid.toUpperCase()}?year=${job.year}&tab=raw`;
    const formats = [
      ...new Set(
        job.rawFiles
          ?.map((file) => file.metadata?.leaf?.fileFormat)
          .filter(Boolean),
      ),
    ].join(', ');

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          width: '100%',
        }}
      >
        <Typography>
          <Link
            href={tfsLink}
            color="inherit"
            target="_blank"
            onClick={(e) => e.stopPropagation()}
          >
            {`${job.year} ${job.crop} yield`}
          </Link>
        </Typography>
        <Chip label={job.status} size="small" />
        <Typography variant="body2" color="text.secondary">
          Coverage: {Math.round(parseFloat(job.coverage))}%
        </Typography>
        {formats && (
          <Typography variant="body2" color="text.secondary">
            Format: {formats}
          </Typography>
        )}
      </Box>
    );
  }

  function buildPlantingJobDetails(job) {
    if (!job.rawFiles?.[0]?.url) return null;

    const filePrefix = `${field.grower.operationName}-${field.fieldName}-planting-${job.year}-${job.crop}`;
    const [, s3Prefix] = /(.*)\/raw\//.exec(job.rawFiles[0].url);
    const rawFile = `${s3Prefix}/${field.aaid.toUpperCase()}/input.csv`;
    const cleanFile = `${s3Prefix}/${field.aaid.toUpperCase()}/planting-0/cleaned.csv`;

    return (
      <>
        <Divider sx={{ mt: -1 }} />
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <DownloadLink
              url={rawFile}
              displayName={`Raw Points`}
              downloadFilename={`${filePrefix}-raw.csv`}
            />
          </Grid>
          <Grid item xs={3}>
            <DownloadLink
              url={cleanFile}
              displayName={`Clean Points`}
              downloadFilename={`${filePrefix}-clean.csv`}
            />
          </Grid>
        </Grid>
        <Divider />
      </>
    );
  }

  function buildYieldJobDetails(job) {
    const filePrefix = `${field.grower.operationName}-${field.fieldName}-yield-${job.year}-${job.crop}`;
    const [, s3Prefix] = /(.*)\/raw\//.exec(job.rawFiles[0].url);
    const rawFile = `${s3Prefix}/${field.aaid.toUpperCase()}/${job.crop}_combined.txt`;
    const cleanFile = `${s3Prefix}/${field.aaid.toUpperCase()}/Cleaned/output.txt`;
    const leafRawFile = `${s3Prefix}/${field.aaid.toUpperCase()}/leaf/${job.crop}_combined.txt`;
    const leafCleanFile = `${s3Prefix}/${field.aaid.toUpperCase()}/Cleaned/leaf/output.txt`;

    return (
      <>
        <Divider sx={{ mt: -1 }} />
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="body2" color="text.secondary">
              Indexed
            </Typography>
            <Typography variant="body2">
              {job.indexed ? new Date(job.indexed).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" color="text.secondary">
              Associated
            </Typography>
            <Typography variant="body2">
              {job.associated
                ? new Date(job.associated).toLocaleString()
                : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" color="text.secondary">
              Cleaned
            </Typography>
            <Typography variant="body2">
              {job.cleaned ? new Date(job.cleaned).toLocaleString() : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" color="text.secondary">
              Interpolated
            </Typography>
            <Typography variant="body2">
              {job.interpolated
                ? new Date(job.interpolated).toLocaleString()
                : 'N/A'}
            </Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <DownloadLink
              url={rawFile}
              displayName={`Raw Points`}
              downloadFilename={`${filePrefix}-raw.csv`}
            />
          </Grid>
          <Grid item xs={3}>
            <DownloadLink
              url={cleanFile}
              displayName={`Clean Points`}
              downloadFilename={`${filePrefix}-clean.csv`}
            />
          </Grid>
          {!isUseLeaf && (
            <Grid item xs={3}>
              <DownloadLink
                url={leafRawFile}
                displayName={`Leaf Raw Points`}
                downloadFilename={`${filePrefix}-leaf-raw.csv`}
              />
            </Grid>
          )}
          {!isUseLeaf && (
            <Grid item xs={3}>
              <DownloadLink
                url={leafCleanFile}
                displayName={`Leaf Clean Points`}
                downloadFilename={`${filePrefix}-leaf-clean.csv`}
              />
            </Grid>
          )}
        </Grid>
        <Divider />
      </>
    );
  }

  function buildJobUploads(job, type) {
    if (!job.uploads?.length) return null;

    return (
      <>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Uploads ({job.uploads.length})
        </Typography>
        <SimpleTable
          component={Paper}
          variant="outlined"
          size="small"
          rows={job.uploads.map((upload) => ({
            Filename: (
              <DownloadLink
                url={upload.originalFile}
                displayName={upload.filename}
              />
            ),
            Files: upload.fileCount,
            Status: upload.status ? (
              <Chip label={upload.status} size="small" />
            ) : (
              'N/A'
            ),
            ...(!isUseLeaf && {
              'Leaf Status': upload.leafConversion?.status ? (
                <Chip
                  label={upload.leafConversion.status.toLowerCase()}
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
              ) : (
                'N/A'
              ),
            }),
            'Uploaded At': new Date(upload.createdAt).toLocaleString(),
            'Uploaded By': upload.createdBy,
            ...(!isUseLeaf && {
              Actions: (
                <IconButton
                  size="small"
                  disabled={working}
                  onClick={() =>
                    setConfirmResend({
                      type,
                      id: upload.objectId,
                      filename: upload.filename,
                    })
                  }
                  title="Resend to Leaf"
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              ),
            }),
          }))}
        />
      </>
    );
  }

  function buildJobRawFiles(job, type) {
    if (!job.rawFiles?.length) return null;

    return (
      <>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Raw Files ({job.rawFiles.length})
        </Typography>
        <SimpleTable
          component={Paper}
          variant="outlined"
          size="small"
          rows={orderBy(job.rawFiles, ['updatedAt'], ['desc']).map((file) => ({
            Filename: (
              <DownloadLink url={file.url} displayName={file.filename} />
            ),
            ...(file.updatedAt && {
              'Converted At': new Date(file.updatedAt).toLocaleString(),
            }),
            'Converted By': <Converter file={file} />,
            Format: file.metadata?.leaf?.fileFormat || '',
            ...(type === 'yield'
              ? { 'Harvest Date': file.harvestDate }
              : {
                  'Plant Date': file.plantDate
                    ? new Date(
                        file.plantDate.split('+')[0].trim(),
                      ).toLocaleDateString()
                    : '',
                }),
            Crop: file.crop,
            Points:
              file.metadata?.stats?.wetMass?.count ||
              file.metadata?.stats?.appliedRate?.count ||
              'N/A',
            Uploads: file.uploads?.length || 0,
            Actions: (
              <IconButton
                size="small"
                disabled={working}
                onClick={() =>
                  setConfirmDelete({
                    type,
                    id: file.objectId,
                    filename: file.filename,
                  })
                }
                title="Delete raw file"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            ),
          }))}
        />
      </>
    );
  }

  function buildJobMetadata(job) {
    if (!job.metadata) return null;

    return (
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Metadata</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <pre
            style={{
              margin: 0,
              fontSize: '0.75rem',
              overflow: 'auto',
            }}
          >
            {JSON.stringify(job.metadata, null, 2)}
          </pre>
        </AccordionDetails>
      </Accordion>
    );
  }
}
